import type { EmailVerification, IAddress } from '@feathr/blackbox';

export interface ISenderInfoPatch {
  /** From name to apply to the campaign or message. */
  from_name?: string;
  /** Postal address to apply to the campaign or message. */
  address?: IAddress;
}

interface IComputeSenderInfoPatchInput {
  /** The from address the effect is currently processing. */
  fromAddress: string | undefined;
  /** The from address the effect last processed (undefined on first run after mount). */
  previousAddress: string | undefined;
  /** The verification record fetched for `fromAddress`, if any. */
  emailVerification: EmailVerification | undefined;
  /** Current `from_name` on the campaign/message model. */
  currentFromName: string | undefined;
  /** Current `address` on the campaign/message model, if any. */
  currentAddress?: IAddress;
}

/**
 * Computes the sender-info patch to apply to a campaign or partner message
 * model after its email verification resolves. Preserves the current
 * `from_name` and `address` values unless the user has actually just changed
 * the email — only a real email change overwrites them with the verification's
 * stored defaults. On initial mount and on remount with the same address,
 * current values are passed through so any in-progress user edits survive
 * step navigation.
 */
export function computeSenderInfoPatch({
  fromAddress,
  previousAddress,
  emailVerification,
  currentFromName,
  currentAddress,
}: IComputeSenderInfoPatchInput): ISenderInfoPatch {
  const patch: ISenderInfoPatch = {
    from_name: currentFromName,
    address: currentAddress,
  };
  if (emailVerification?.get('status') !== 'Success') {
    return patch;
  }
  const didEmailChange = previousAddress !== undefined && previousAddress !== fromAddress;
  if (didEmailChange) {
    patch.from_name = emailVerification.get('from_name');
    patch.address = emailVerification.get('address');
  }
  return patch;
}
